﻿import {RecaptchaConfigModel} from './recaptcha.model';

export interface IRecaptchaService {
    create(elm:any, conf:RecaptchaConfigModel):ng.IPromise<any>;
    reload(widgetId:string):void;
    useLang(widgetId:string, lang:string):string;
    getResponse(widgetId:string):string;
    getInstance(widgetId:string):any;
    destroy(widgetId:string):void;
}

export interface IRecaptchaProvider extends angular.IServiceProvider {
    setDefaults(defaults: RecaptchaConfigModel): void;
    setSiteKey(siteKey: string):void;
    setTheme(theme: string): void;
    setStoken(stoken: string): void;
    setSize(size: string): void;
    setLang(lang: string): void;
    setOnLoadFunctionName(onLoadFunctionName: string):void;
}

export class RecaptchaService implements IRecaptchaProvider {
    //converted from:https://github.com/VividCortex/angular-recaptcha

    private $rootScope: ng.IRootScopeService;
    private $window: ng.IWindowService;
    private $q: ng.IQService;
    private $document: ng.IDocumentService;

    private config: RecaptchaConfigModel = new RecaptchaConfigModel();
    private onLoadFunctionName: string = 'recaptchaApiLoaded';

    public constructor() {
        this.$get.$inject = ['$rootScope','$window','$q','$document'];
    }

    public setDefaults(defaults: RecaptchaConfigModel) {
        angular.copy(defaults,this.config);
    }

    public setSiteKey(siteKey: string) {
        this.config.sitekey = siteKey;
    }

    public setTheme(theme: string) {
        this.config.theme = theme;
    }

    public setStoken(stoken:string) {
        this.config.stoken = stoken;
    }

    public setSize(size: string) {
        this.config.size = size;
    }

    public setLang(lang: string) {
        this.config.hl = lang;
    }

    public setOnLoadFunctionName(onLoadFunctionName: string) {
        this.onLoadFunctionName = onLoadFunctionName;
    }

    
    public $get($rootScope: ng.IRootScopeService, $window: ng.IWindowService, $q: ng.IQService, $document: ng.IDocumentService):IRecaptchaService {
        this.$rootScope = $rootScope;
        this.$window = $window;
        this.$q = $q;
        this.$document = $document;

        var deferred = this.$q.defer();
        var promise = deferred.promise;
        var instances: any = {};
        var recaptcha;
        var window = this.$window as any;
        
        window.recaptchaApiLoadedCallback = window.recaptchaApiLoadedCallback || [];

        const callback = () => {
            recaptcha = window.grecaptcha;
            deferred.resolve(recaptcha);
        };

        window.recaptchaApiLoadedCallback.push(callback);

        window[this.onLoadFunctionName] = () => {
            window.recaptchaApiLoadedCallback.forEach((cb) => {
                cb();
            });
        }

        function getRecaptcha():ng.IPromise<any> {
            if (!!recaptcha) {
                return $q.when(recaptcha);
            }
            return promise;
        }

        function validateRecaptchaInstance():void {
            if (!recaptcha) {
                throw new Error('recaptcha has not been loaded yet');
            }
        }

        if (angular.isDefined(window.grecaptcha)) {
            callback();
        } else {
            var script = window.document.createElement('script');
            script.async = true;
            script.defer = true;
            script.src = 'https://www.google.com/recaptcha/api.js?onload=' + this.onLoadFunctionName + '&render=explicit';
            this.$document.find('body').append(script);
        }

        return {
            create: (elm: any, conf: RecaptchaConfigModel): ng.IPromise<any> => {
                conf.sitekey = conf.sitekey || this.config.sitekey;
                conf.theme = conf.theme || this.config.theme;
                conf.stoken = conf.stoken || this.config.stoken;
                conf.size = conf.size || this.config.size;
                conf.type = conf.type || this.config.type;
                conf.hl = conf.hl || this.config.hl;

                if (!conf.sitekey || conf.sitekey.length !== 40) {
                    throw new Error('You need to set the "key" attribute to your public reCaptcha key. If you don\'t have a key, please get one from https://www.google.com/recaptcha/admin/create');
                }
                return getRecaptcha().then((recaptcha) => {
                    var widgetId = recaptcha.render(elm, conf);
                    instances[widgetId] = elm;
                    return widgetId;
                });
            },
            reload: (widgetId:string):void => {
                validateRecaptchaInstance();

                recaptcha.reset(widgetId);

                // Let everyone know this widget has been reset.
                $rootScope.$broadcast('reCaptchaReset', widgetId);
            },
            useLang: (widgetId:string, lang:string):string => {
                var instance = instances[widgetId];

                if (instance) {
                    var iframe = instance.querySelector('iframe');
                    if (lang) {
                        // Setter
                        if (iframe && iframe.src) {
                            var s = iframe.src;
                            if (/[?&]hl=/.test(s)) {
                                s = s.replace(/([?&]hl=)\w+/, '$1' + lang);
                            } else {
                                s += ((s.indexOf('?') === -1) ? '?' : '&') + 'hl=' + lang;
                            }

                            iframe.src = s;
                        }
                    } else {
                        // Getter
                        if (iframe && iframe.src && /[?&]hl=\w+/.test(iframe.src)) {
                            return iframe.src.replace(/.+[?&]hl=(\w+)([^\w].+)?/, '$1');
                        } else {
                            return null;
                        }
                    }
                } else {
                    throw new Error(`reCaptcha Widget ID not exists: ${widgetId}`);
                }
            },
            getResponse: (widgetId:string):string => {
                validateRecaptchaInstance();

                return recaptcha.getResponse(widgetId);
            },
            getInstance: (widgetId:string):any => {
                return instances[widgetId];
            },
            destroy: (widgetId:string):void => {
                delete instances[widgetId];
            }
        }

    }
}